export interface MarkRect {
  top: number
  left: number
  width: number
  height: number
}

export function findMarkRange(
  text: string,
  markText: string,
): { start: number; end: number } | null {
  if (!text || !markText) return null
  const start = text.indexOf(markText)
  if (start < 0) return null
  return { start, end: start + markText.length }
}

function toRelativeRects(
  clientRects: ArrayLike<DOMRect>,
  containerBox: DOMRect,
): MarkRect[] {
  return Array.from(clientRects)
    .filter((rect) => rect.width > 0 && rect.height > 0)
    .map((rect) => ({
      top: rect.top - containerBox.top,
      left: rect.left - containerBox.left,
      width: rect.width,
      height: rect.height,
    }))
}

function collectTextNodes(root: Node): Text[] {
  const nodes: Text[] = []
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let node: Node | null
  while ((node = walker.nextNode())) {
    if (node.nodeValue) nodes.push(node as Text)
  }
  return nodes
}

function rangeForNeedle(root: HTMLElement, needle: string): Range | null {
  const nodes = collectTextNodes(root)
  if (nodes.length === 0) return null

  let haystack = ""
  const spans: Array<{ node: Text; start: number; end: number }> = []
  for (const node of nodes) {
    const start = haystack.length
    haystack += node.data
    spans.push({ node, start, end: haystack.length })
  }

  const idx = haystack.indexOf(needle)
  if (idx < 0) return null
  const end = idx + needle.length
  const startSpan = spans.find((span) => idx >= span.start && idx < span.end)
  const endSpan = [...spans]
    .reverse()
    .find((span) => end > span.start && end <= span.end)
  if (!startSpan || !endSpan) return null

  const range = document.createRange()
  range.setStart(startSpan.node, idx - startSpan.start)
  range.setEnd(endSpan.node, end - endSpan.start)
  return range
}

const FIELD_STYLE_KEYS = [
  "font",
  "fontSize",
  "fontFamily",
  "fontWeight",
  "fontStyle",
  "letterSpacing",
  "textTransform",
  "wordSpacing",
  "lineHeight",
  "padding",
  "border",
  "boxSizing",
] as const

function measureFormField(
  field: HTMLInputElement | HTMLTextAreaElement,
  needle: string,
  containerBox: DOMRect,
): MarkRect[] {
  const range = findMarkRange(field.value, needle)
  if (!range) return []

  const style = getComputedStyle(field)
  const mirror = document.createElement("div")
  mirror.setAttribute("aria-hidden", "true")
  mirror.style.position = "absolute"
  mirror.style.visibility = "hidden"
  mirror.style.pointerEvents = "none"
  mirror.style.whiteSpace =
    field instanceof HTMLTextAreaElement ? "pre-wrap" : "pre"
  mirror.style.overflow = "hidden"
  mirror.style.width = `${field.clientWidth}px`
  for (const key of FIELD_STYLE_KEYS) {
    mirror.style[key] = style[key]
  }

  const before = document.createElement("span")
  before.textContent = field.value.slice(0, range.start)
  const target = document.createElement("span")
  target.textContent = field.value.slice(range.start, range.end)
  mirror.append(before, target)
  document.body.append(mirror)

  const fieldBox = field.getBoundingClientRect()
  const targetBox = target.getBoundingClientRect()
  const mirrorBox = mirror.getBoundingClientRect()
  document.body.removeChild(mirror)

  const lineHeight = Number.parseFloat(style.lineHeight) || targetBox.height
  return [
    {
      top:
        fieldBox.top -
        containerBox.top +
        (targetBox.top - mirrorBox.top) -
        field.scrollTop,
      left:
        fieldBox.left -
        containerBox.left +
        (targetBox.left - mirrorBox.left) -
        field.scrollLeft,
      width: targetBox.width,
      height: Math.max(targetBox.height, lineHeight),
    },
  ]
}

export function measureMarkedText(
  container: HTMLElement,
  markText: string,
): MarkRect[] {
  const needle = markText.trim()
  if (!needle) return []

  const containerBox = container.getBoundingClientRect()
  const editor = container.querySelector<HTMLElement>(
    ".tiptap, [contenteditable='true']",
  )
  if (editor) {
    const range = rangeForNeedle(editor, needle)
    if (range) {
      const rects = toRelativeRects(range.getClientRects(), containerBox)
      if (rects.length > 0) return rects
    }
  }

  const fields = container.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement
  >("input, textarea")
  for (const field of fields) {
    const rects = measureFormField(field, needle, containerBox)
    if (rects.length > 0) return rects
  }

  return []
}

export function lastMarkedTextRect(
  container: HTMLElement,
  markText: string,
): MarkRect | null {
  const rects = measureMarkedText(container, markText)
  return rects[rects.length - 1] ?? null
}

function unionMarkRects(rects: MarkRect[]): MarkRect | null {
  if (rects.length === 0) return null
  if (rects.length === 1) return rects[0]!
  const top = Math.min(...rects.map((rect) => rect.top))
  const left = Math.min(...rects.map((rect) => rect.left))
  const right = Math.max(...rects.map((rect) => rect.left + rect.width))
  const bottom = Math.max(...rects.map((rect) => rect.top + rect.height))
  return { top, left, width: right - left, height: bottom - top }
}

function measureEditorMirrorTextRect(
  editor: HTMLElement,
  text: string,
  container: HTMLElement,
): MarkRect | null {
  const sample = text.trim()
  if (!sample) return null

  const containerBox = container.getBoundingClientRect()
  const editorBox = editor.getBoundingClientRect()
  const style = getComputedStyle(editor)
  const mirror = document.createElement("div")
  mirror.setAttribute("aria-hidden", "true")
  mirror.style.position = "absolute"
  mirror.style.visibility = "hidden"
  mirror.style.pointerEvents = "none"
  mirror.style.whiteSpace = "pre-wrap"
  mirror.style.overflow = "hidden"
  mirror.style.width = `${editor.clientWidth}px`
  for (const key of FIELD_STYLE_KEYS) {
    mirror.style[key] = style[key]
  }

  const target = document.createElement("span")
  target.textContent = text
  mirror.append(target)
  document.body.append(mirror)

  const targetBox = target.getBoundingClientRect()
  const mirrorBox = mirror.getBoundingClientRect()
  document.body.removeChild(mirror)

  if (targetBox.width <= 0 || targetBox.height <= 0) return null

  return {
    top:
      editorBox.top -
      containerBox.top +
      (targetBox.top - mirrorBox.top) -
      editor.scrollTop,
    left:
      editorBox.left -
      containerBox.left +
      (targetBox.left - mirrorBox.left) -
      editor.scrollLeft,
    width: targetBox.width,
    height: targetBox.height,
  }
}

/**
 * Ink bounds for Leo apply motion. Prefer live DOM ranges; fall back to a
 * mirror when TipTap has not committed yet.
 */
export function measureInkTextRect(
  container: HTMLElement,
  text: string,
): MarkRect | null {
  const needle = text.trim()
  if (!needle) return null

  const marked = measureMarkedText(container, needle)
  if (marked.length > 0) return unionMarkRects(marked)

  const editor = container.querySelector<HTMLElement>(
    ".tiptap, [contenteditable='true']",
  )
  if (editor) return measureEditorMirrorTextRect(editor, text, container)

  const field = container.querySelector<
    HTMLInputElement | HTMLTextAreaElement
  >("input, textarea")
  if (field) return measureFieldTextRect(field, text, container)

  return null
}

/** Glyph bounds for a string rendered in a form field, relative to `container`. */
export function measureFieldTextRect(
  field: HTMLInputElement | HTMLTextAreaElement,
  text: string,
  container: HTMLElement,
): MarkRect | null {
  const sample = text.trim()
  if (!sample) return null

  const containerBox = container.getBoundingClientRect()
  const style = getComputedStyle(field)
  const mirror = document.createElement("div")
  mirror.setAttribute("aria-hidden", "true")
  mirror.style.position = "absolute"
  mirror.style.visibility = "hidden"
  mirror.style.pointerEvents = "none"
  mirror.style.whiteSpace =
    field instanceof HTMLTextAreaElement ? "pre-wrap" : "pre"
  mirror.style.overflow = "hidden"
  mirror.style.width = `${field.clientWidth}px`
  for (const key of FIELD_STYLE_KEYS) {
    mirror.style[key] = style[key]
  }

  const target = document.createElement("span")
  target.textContent = text
  mirror.append(target)
  document.body.append(mirror)

  const fieldBox = field.getBoundingClientRect()
  const targetBox = target.getBoundingClientRect()
  const mirrorBox = mirror.getBoundingClientRect()
  document.body.removeChild(mirror)

  if (targetBox.width <= 0 || targetBox.height <= 0) return null

  const lineHeight = Number.parseFloat(style.lineHeight) || targetBox.height
  return {
    top:
      fieldBox.top -
      containerBox.top +
      (targetBox.top - mirrorBox.top) -
      field.scrollTop,
    left:
      fieldBox.left -
      containerBox.left +
      (targetBox.left - mirrorBox.left) -
      field.scrollLeft,
    width: targetBox.width,
    height: Math.max(targetBox.height, lineHeight),
  }
}
